/**
 * ĐĂNG KÝ KHUÔN MẶT — docs/07 §7.5, docs/10 P0-2 bước 5.
 *
 * Thứ tự BẮT BUỘC: đọc và ký đồng ý → chụp tối đa 3 ảnh (chính diện, hơi
 * nghiêng, đeo kính nếu hay đeo). Server từ chối đăng ký khi chưa có đồng ý
 * (chốt ở tầng DB), màn hình này chỉ dẫn đúng thứ tự đó.
 *
 * Ảnh đăng ký KHÔNG được lưu ở server — chỉ lưu vector đặc trưng. Câu đó nằm
 * trong văn bản đồng ý, và phải đúng như vậy ở backend (routers/biometric.py).
 */

import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../api/loi'
import { layTrangThaiFace } from '../services/catalog.service'
import {
  dangKyKhuonMat, dongYSinhTrac, rutDongYSinhTrac, thuHoiMauKhuonMat,
} from '../services/face.service'
import type { TrangThaiFace } from '../types'
import { laNguCanhBaoMat } from '../lib/camera'
import { ngayGio } from '../lib/gio'
import ChupKhuonMat from './ChupKhuonMat'

type Pha = 'dang_tai' | 'xem' | 'doc_dong_y' | 'dang_chup' | 'dang_gui'

export default function DangKyKhuonMat() {
  const [tt, setTt] = useState<TrangThaiFace | null>(null)
  const [pha, setPha] = useState<Pha>('dang_tai')
  const [loi, setLoi] = useState<string | null>(null)
  const [thongBao, setThongBao] = useState<string | null>(null)
  const [daDoc, setDaDoc] = useState(false)
  const [dangXuLy, setDangXuLy] = useState(false)

  const nap = useCallback(async () => {
    try {
      setTt(await layTrangThaiFace())
      setLoi(null)
    } catch (e) {
      setLoi(e instanceof ApiError
        ? e.message
        : 'Không tải được trạng thái khuôn mặt. Kiểm tra kết nối mạng.')
    } finally {
      setPha('xem')
    }
  }, [])

  useEffect(() => { void nap() }, [nap])

  const kyDongY = async () => {
    if (!tt) return
    setDangXuLy(true)
    setLoi(null)
    try {
      const kq = await dongYSinhTrac(tt.consent_version_hien_tai)
      setThongBao(kq.phai_dang_ky_lai
        ? 'Đã ký bản đồng ý mới. Mẫu cũ đã bị vô hiệu, bạn cần đăng ký lại khuôn mặt.'
        : 'Đã ghi nhận đồng ý. Bây giờ chụp ảnh để đăng ký khuôn mặt.')
      setDaDoc(false)
      await nap()
    } catch (e) {
      setLoi(e instanceof ApiError ? e.message : 'Không gửi được. Thử lại.')
      setPha('xem')
    } finally {
      setDangXuLy(false)
    }
  }

  const guiAnh = async (anh: Blob) => {
    setPha('dang_gui')
    setLoi(null)
    try {
      const kq = await dangKyKhuonMat(anh)
      setThongBao(kq.con_lai > 0
        ? `Đã lưu mẫu ${kq.so_mau}/${tt?.so_mau_toi_da ?? 3}. Chụp thêm ${kq.con_lai} ảnh `
          + 'ở góc hơi khác (hoặc đeo kính nếu bạn hay đeo) để nhận diện chính xác hơn — không bắt buộc.'
        : `Đã đủ ${kq.so_mau} mẫu. Bạn có thể chấm công tại công trình.`)
      await nap()
    } catch (e) {
      // Server nói rõ vì sao (ảnh mờ, nhiều người, không giống mẫu trước…) —
      // hiện nguyên văn, đó là hướng dẫn cho lần chụp tiếp theo.
      setLoi(e instanceof ApiError ? e.message : 'Không gửi được ảnh. Kiểm tra mạng rồi thử lại.')
      setPha('xem')
    }
  }

  const thuHoi = async () => {
    if (!window.confirm('Thu hồi toàn bộ mẫu khuôn mặt để đăng ký lại?')) return
    setDangXuLy(true)
    try {
      await thuHoiMauKhuonMat()
      setThongBao('Đã thu hồi mẫu cũ. Chụp ảnh để đăng ký lại.')
      await nap()
    } catch (e) {
      setLoi(e instanceof ApiError ? e.message : 'Không thu hồi được. Thử lại.')
    } finally {
      setDangXuLy(false)
    }
  }

  const rutDongY = async () => {
    if (!window.confirm(
      'Rút lại đồng ý sẽ vô hiệu toàn bộ mẫu khuôn mặt ngay lập tức. '
      + 'Sau đó, chấm công tại công trình sẽ cần tổ trưởng hoặc quản lý xác nhận. Tiếp tục?',
    )) return
    setDangXuLy(true)
    try {
      await rutDongYSinhTrac('Nhân viên rút lại đồng ý trong ứng dụng')
      setThongBao('Đã rút lại đồng ý. Toàn bộ mẫu khuôn mặt đã bị vô hiệu.')
      await nap()
    } catch (e) {
      setLoi(e instanceof ApiError ? e.message : 'Không gửi được. Thử lại.')
    } finally {
      setDangXuLy(false)
    }
  }

  // ── Màn chụp toàn màn hình ───────────────────────────────────────────────
  if (pha === 'dang_chup' && tt) {
    const thu = tt.so_mau + 1
    return (
      <ChupKhuonMat
        tieuDe={`Đăng ký khuôn mặt — ảnh ${thu}/${tt.so_mau_toi_da}`}
        huongDan={thu === 1
          ? 'Nhìn thẳng vào camera, đủ sáng, bỏ khẩu trang và kính râm.'
          : 'Chụp ở góc hơi khác lần trước, hoặc đeo kính nếu bạn hay đeo.'}
        onXong={(anh) => void guiAnh(anh)}
        onHuy={() => setPha('xem')}
      />
    )
  }

  if (pha === 'dang_tai') return <p className="ghi-chu">Đang tải…</p>
  if (pha === 'dang_gui') return <p className="trang-thai-cho">Đang xử lý ảnh…</p>

  // ── Đọc và ký văn bản đồng ý ─────────────────────────────────────────────
  if (pha === 'doc_dong_y' && tt) {
    return (
      <div>
        <pre className="van-ban-dong-y">{tt.van_ban_dong_y}</pre>
        <label className="o-tick">
          <input
            type="checkbox"
            checked={daDoc}
            onChange={(e) => setDaDoc(e.target.checked)}
          />
          <span>Tôi đã đọc và <strong>tự nguyện</strong> đồng ý cho công ty xử lý
            dữ liệu khuôn mặt của tôi theo nội dung trên.</span>
        </label>
        {loi && <p className="thong-bao-loi" role="alert">{loi}</p>}
        <div className="cum-nut">
          <button type="button" className="nut-gui" disabled={!daDoc || dangXuLy}
            onClick={() => void kyDongY()}>
            {dangXuLy ? 'Đang gửi…' : 'Đồng ý'}
          </button>
          <button type="button" className="nut-phu" onClick={() => setPha('xem')}>
            Để sau
          </button>
        </div>
      </div>
    )
  }

  // ── Trạng thái ──────────────────────────────────────────────────────────
  if (!tt) {
    return (
      <div>
        <p className="thong-bao-loi" role="alert">{loi ?? 'Không tải được trạng thái.'}</p>
        <button type="button" className="nut-phu" onClick={() => void nap()}>Tải lại</button>
      </div>
    )
  }

  return (
    <div>
      {thongBao && <p className="thong-bao-tot" role="status">{thongBao}</p>}
      {loi && <p className="thong-bao-loi" role="alert">{loi}</p>}

      {/* Cảnh báo TRƯỚC khi bấm nút. Không có nó, nhân viên ký đồng ý xong,
          bấm "Đăng ký khuôn mặt", rồi mới phát hiện camera không mở được —
          và tưởng phần mềm hỏng. */}
      {!laNguCanhBaoMat() && (
        <p className="thong-bao-loi" role="alert">
          <strong>Chưa đăng ký khuôn mặt được từ địa chỉ này.</strong> Trình
          duyệt chỉ cho mở camera khi trang chạy trên HTTPS. Hãy mở lại ứng
          dụng bằng địa chỉ bắt đầu bằng <code>https://</code> — hỏi quản trị
          đường dẫn HTTPS của công ty.
        </p>
      )}

      <div className="face-hang">
        <span className="face-nhan">Đồng ý xử lý dữ liệu</span>
        <span className="face-gia-tri">
          {tt.da_dong_y
            ? `Đã ký ${tt.consented_at ? ngayGio(tt.consented_at) : ''}`
            : 'Chưa ký'}
        </span>
      </div>
      <div className="face-hang">
        <span className="face-nhan">Mẫu khuôn mặt</span>
        <span className="face-gia-tri">{tt.so_mau}/{tt.so_mau_toi_da}</span>
      </div>

      {!tt.da_dong_y ? (
        <>
          <p className="ghi-chu">
            Chấm công tại công trình bắt buộc xác minh khuôn mặt. Trước khi đăng
            ký, bạn cần đọc và ký văn bản đồng ý (Nghị định 13/2023/NĐ-CP).
            Nhân viên chỉ làm việc ở văn phòng không cần bước này.
          </p>
          <div className="cum-nut">
            <button type="button" className="nut-gui" onClick={() => setPha('doc_dong_y')}>
              Đọc và ký đồng ý
            </button>
          </div>
        </>
      ) : (
        <>
          {tt.can_dong_y_lai && (
            <p className="canh-bao-nho" role="alert">
              Văn bản đồng ý có phiên bản mới. Bạn cần ký lại, sau đó đăng ký lại khuôn mặt.
            </p>
          )}
          {tt.so_mau === 0 && !tt.can_dong_y_lai && (
            <p className="ghi-chu">
              Chưa có mẫu nào. Chụp ít nhất 1 ảnh để chấm công được tại công trình.
            </p>
          )}
          <div className="cum-nut">
            {tt.can_dong_y_lai && (
              <button type="button" className="nut-gui" onClick={() => setPha('doc_dong_y')}>
                Ký lại văn bản đồng ý
              </button>
            )}
            {!tt.can_dong_y_lai && tt.so_mau < tt.so_mau_toi_da && (
              <button type="button" className="nut-gui" disabled={dangXuLy}
                onClick={() => { setThongBao(null); setPha('dang_chup') }}>
                {tt.so_mau === 0 ? 'Chụp ảnh đăng ký' : `Chụp thêm ảnh ${tt.so_mau + 1}/${tt.so_mau_toi_da}`}
              </button>
            )}
            {tt.so_mau > 0 && (
              <button type="button" className="nut-phu" disabled={dangXuLy}
                onClick={() => void thuHoi()}>
                Thu hồi mẫu để đăng ký lại
              </button>
            )}
            <button type="button" className="nut-nguy-hiem" disabled={dangXuLy}
              onClick={() => void rutDongY()}>
              Rút lại đồng ý
            </button>
          </div>
        </>
      )}
    </div>
  )
}
